<template lang="pug">

	div#mapa

</template>

<script>

import Vue from 'vue';

import L from 'leaflet';
import leafletUtils from '@/utils/leafletUtils';
import 'leaflet-draw/dist/leaflet.draw-src.js';
import '@/utils/leaflet-beaultify-marker-icon.js';
import CustomBoxZoom from '@/utils/customBoxZoom';
import MiniMapa from '@/utils/leaflet-minimap';
import Config from '@/config.js';
import camadasService from '@/services/camadasService';
import { mapMutations, mapState } from 'vuex';
import 'leaflet-contextmenu/dist/leaflet.contextmenu.min.js';
import markerStyles from '@/utils/markerStyles';
import Estilo from '@/models/estilo';
import DrawUtils from '@/utils/drawUtils';

export default {
	
	name: 'Mapa',

	data () {
		return {
			mapa: null,
			selectedBaseMap: null,
			baseMapLayer: null,
			boxZoom: null,
			markers: {},
			miniMapa: null,
			draw: {
				tools: {},
				layer: null,
				options: null
			},
			layers: {},
			layersMarkersActive: false
		};
	},

	methods: {

		/**
		 * Responsável pela criação inicial do mapa,
		 * - Cria a configuração default do mapa
		 * - Adicionar a baselayer inicial
		 * - Registra os eventos de interação com o mapa
		 */
		createMap () {
			
			// Criando mapa
			this.mapa = L.map('mapa', {
				center: Config.center,
				zoom: Config.zoom,
				minZoom: this.selectedBaseMap.minZoom,
				maxZoom: this.selectedBaseMap.maxZoom,
				boxZoom: true,
				doubleClickZoom: false,
				zoomControl: false,
				contextmenu: true,
				contextmenuWidth: 140,
				preferCanvas: true,
				contextmenuItems: [
					{
						text: 'O que é?',
						callback: this.getInformacoesLayer
					},
					{
						text: 'Inspecionar',
						callback: this.inspecionar
					}
				]
			});

			Vue.prototype.$mapa = this.mapa;

			this.boxZoom = new CustomBoxZoom(this.mapa);

			// Adicionando mapa de fundo
			this.baseMapLayer = new L.TileLayer(this.selectedBaseMap.url,
				{
					attribution: this.selectedBaseMap.attribution,
					tms: this.selectedBaseMap.tms,
					minZoom: this.selectedBaseMap.minZoom,
					maxZoom: this.selectedBaseMap.maxZoom
				});

			this.mapa.addLayer(this.baseMapLayer);
			
			L.control.scale({imperial: false}).addTo(this.mapa);

			this.miniMapa = new MiniMapa(this.mapa);

			this.registerEvents();

			this.adicionarFuncionalidadeDesenho();
		},

		/**
		 * Busca as informações da layer que encontra-se mais ao topo
		 */
		getInformacoesLayer (e) {

			this.$bus.$emit('informacoesPonto.show', { latlng: e.latlng });
		},

		/**
		 * Busca as informações de todas as layers ativas
		 */
		inspecionar (e) {
			this.$bus.$emit('inspecao.show', { latlng: e.latlng, inspecionar: true });
		},

		/**
		 * Registra todos os eventos de responsabilidade do mapa
		 */
		registerEvents () {

			this.$bus.$on('mapa.zoomIn', () => this.mapa.zoomIn());
			this.$bus.$on('mapa.zoomOut', () => this.mapa.zoomOut());
			this.$bus.$on('mapa.initialZoom', () => this.mapa.setView(Config.center, Config.zoom));
			this.$bus.$on('mapa.boxZoom', () => this.boxZoom.enable());
			this.$bus.$on('mapa.changeZoom', (z) => this.mapa.setZoom(z));
			
			this.$bus.$on('mapa.changeBaseMap', this.changeBaseMap);
			
			this.$bus.$on('mapa.addCoordinatePoint', this.addCoordinatePoint);
			this.$bus.$on('mapa.removeCoordinatePoint', this.removeCoordinateMarker);

			this.mapa.on('zoomend', () => this.setMapZoom(this.mapa.getZoom()));
			this.mapa.on('mousemove', (e) => this.setCoordinate(e.latlng));
			this.mapa.on('click', () => this.removeCoordinateMarker(null, true));
			this.mapa.on('dblclick', (e) => this.onDoubleClick(e));
			this.mapa.on('moveend', () => this.atualizarMarcadores());
			this.mapa.on('contextmenu', (e) => this.onContextMenu(e));

			this.$bus.$on('mapa.draw', this.startDraw);
			this.$bus.$on('mapa.cancelDraw', this.cancelDraw);
			this.$bus.$on('mapa.removeLayer', this.removeLayer);
			this.$bus.$on('mapa.addLayer', this.addLayer);
			this.$bus.$on('mapa.centerLayer', this.centerLayer);

			this.$bus.$on('mapa.centerCamada', this.centerCamada);
		},

		/**
		 * Processa o double click no mapa mapa evitar a execução da busca de informações
		 * do ponto caso estiver vetorizando uma geometria.
		 */
		onDoubleClick (e) {
			
			if (this.isDrawToolEnable()) {

				var drawTool = this.getEnabledDrawTool();

				if (drawTool._finishShape) drawTool._finishShape();

			} else {

				this.getInformacoesLayer(e);
			}
		},

		/**
		 * Processa o click do botão direito no mapa mapa evitar a exibição do context menu
		 * caso estiver vetorizando uma geometria
		 */
		onContextMenu (e) {

			if (this.isDrawToolEnable()) this.mapa.contextmenu.hide();
		},

		/**
		 * Adiciona a funcionalidade de base de referência caso esteja configurada
		 */
		adicionarFuncionalidadeDesenho () {
			
			var drawingStyle = { dashArray: '10,10', color: DrawUtils.DEFAULT_COLOR, weight: 2 };
			
			this.draw.tools[ DrawUtils.POLYGON_TOOL ] = new L.Draw.Polygon(this.mapa,
				{
					icon: new L.DivIcon({
						className: 'leaflet-div-icon leaflet-editing-icon leaflet-editing-icon-polygon'
					}),
					
					shapeOptions: drawingStyle
				}
			);

			this.draw.tools[ DrawUtils.RECTANGLE_TOOL ] = new L.Draw.Rectangle(this.mapa, { shapeOptions: drawingStyle });

			this.draw.tools[ DrawUtils.POLYLINE_TOOL ] = new L.Draw.Polyline(this.mapa, { shapeOptions: drawingStyle });

			this.draw.tools[ DrawUtils.CIRCLEMARKER_TOOL ] = new L.Draw.CircleMarker(this.mapa, { shapeOptions: drawingStyle });

			this.mapa.on('draw:created', (e) => this.onDrawCreated(e));
			this.mapa.on('draw:canceled', (e) => this.onDrawCanceled(e));
		},

		/**
		 * Adiciona a geometria após finalização do desenho
		 */
		onDrawCreated (e) {

			this.mapa.addLayer(e.layer);

			if (this.draw.options.center) this.centerLayer(e.layer);

			if (this.draw.options.onComplete) this.draw.options.onComplete(e.layer);
		},

		/**
		 * Trata cancelamento da vetorização (tecla ESC)
		 */
		onDrawCanceled (e) {

			if (this.draw.options.onCancel) this.draw.options.onCancel();
			
			this.draw.options = null;
		},

		/*
		 * Método que ativa/desativa a ferramenta de desenho da base de referência
		 */
		startDraw (options) {

			if (this.isDrawToolEnable()) this.cancelDraw();

			options.tool = options.tool || 'polygon';
			var tool = this.draw.tools[ options.tool ];

			if (!tool) {
				console.error('Não existe a ferramenta de desenho "' + options.tool + '"');
				return;
			}
	
			tool.enable();
			this.draw.options = options;
		},

		/**
		 * Desabilita a ferramenta de desenho.
		 */
		cancelDraw () {

			if (!this.isDrawToolEnable()) return;

			for (let toolName in this.draw.tools) {
				
				let tool = this.draw.tools[toolName];
				if (tool && tool._enabled) tool.disable();
			}
				
			this.mapa.fire('draw:canceled');
		},

		/**
		 * Desabilita todas as ferramentas de desenho
		 */
		disableDrawTools () {

			for (let toolName in this.draw.tools) {
				
				let tool = this.draw.tools[toolName];
				if (tool && tool._enabled) tool.disable();
			}
		},

		/**
		 * Centraliza o mapa na Layer que contém a geometria da base de referência
		 */
		centerLayer (layer) {
			
			this.mapa.fitBounds(layer.getBounds());
		},

		/**
		 * Centraliza o mapa na camada
		 */
		centerCamada (camada) {
			
			if (!camada || !camada.id || !this.layers[camada.id]) return;

			var layer = this.layers[camada.id];
			layer = layer.geom || layer.marker;
			
			if (layer) this.mapa.fitBounds(layer.getBounds());
		},

		/**
		 * Remove uma layer do mapa
		 */
		removeLayer (layer) {
			
			if (layer) this.mapa.removeLayer(layer);
		},

		/**
		 * Adiciona uma layer ao mapa
		 */
		addLayer (layer) {
			
			if (layer) this.mapa.addLayer(layer);
		},

		/**
		 * Altera o mapa de fundo
		 */
		changeBaseMap (tileLayer) {

			this.mapa.removeLayer(this.baseMapLayer);

			this.baseMapLayer = new L.TileLayer(tileLayer.url,
				{
					attribution: tileLayer.attribution,
					tms: tileLayer.tms,
					minZoom: tileLayer.minZoom,
					maxZoom: tileLayer.maxZoom
				});

			this.mapa.addLayer(this.baseMapLayer);
			
			// Faz com que a layer fica sempre em baixo das demais
			this.baseMapLayer.bringToBack();

			this.changeMaxZoom(tileLayer.maxZoom);
			this.changeMinZoom(tileLayer.minZoom);
		},

		/*
		 * Altera o valor do máximo do zoom permitido pelo mapa e atualiza o vuex com o novo valor
		 */
		changeMaxZoom (newMaxZoom) {
			
			let currentZoom = this.mapa.getZoom();

			if (currentZoom > newMaxZoom) this.mapa.setZoom(newMaxZoom);

			this.mapa.setMaxZoom(newMaxZoom);

			this.setMaxZoom(newMaxZoom);
		},

		/*
		 * Altera o valor do mínimo do zoom permitido pelo mapa e atualiza o vuex com o novo valor
		 */
		changeMinZoom (newMinZoom) {
			
			let currentZoom = this.mapa.getZoom();

			if (currentZoom < newMinZoom) this.mapa.setZoom(newMinZoom);

			this.mapa.setMinZoom(newMinZoom);

			this.setMinZoom(newMinZoom);
		},

		/**
		 * Adiciona coordenada no mapa
		 */
		addCoordinatePoint (options) {

			if (this.markers[options.markerCode]) {
				this.removeCoordinateMarker(options.markerCode);
			}

			var marker = L.marker([options.lat, options.lng], {
				icon: L.BeautifyIcon.icon(options.markerStyle),
				draggable: false
			});

			marker.addTo(this.mapa);

			this.markers[options.markerCode] = {
				marker: marker,
				fixed: options.fixed
			};

			if (options.center) {
				let latlng = L.latLng(options.lat, options.lng);
				this.mapa.flyTo(latlng);
			}

			if (options.inspecionar) {
				this.inspecionar({
					latlng: { lat: options.lat, lng: options.lng }
				});
			}
		},

		/**
		 * Remove o marcador da coordenada inserida no mapa
		 */
		removeCoordinateMarker (code, onlyTempMarkers) {

			if (code) {

				if (this.markers[code]) {
					this.mapa.removeLayer(this.markers[code].marker);
					delete this.markers[code];
				}
				
			} else {

				for (let markerCode in this.markers) {
					
					let marker = this.markers[markerCode];

					if (!onlyTempMarkers || !marker.fixed) {
						this.mapa.removeLayer(marker.marker);
						delete this.markers[markerCode];
					}
				}
			}
		},

		/**
		 * Atualiza as camadas do mapa de acordo com as camadas ativas.
		 */
		atualizarCamadasAtivas () {

			var oldLayers = this.layers;
			this.layers = {};
			var novasCamadasMarcador = [];

			for (let i = 0; i < this.camadasAtivas.length; i++) {
				
				let camada = this.camadasAtivas[i];
				
				if (!oldLayers[camada.id]) {

					this.adicionarCamada(camada);

					if (camada.visualizacao.marcador) novasCamadasMarcador.push(camada.id);

				} else {

					this.layers[camada.id] = oldLayers[camada.id];
				}

				delete oldLayers[camada.id];
			}

			for (let prop in oldLayers) {
				this.removerCamada(oldLayers[prop]);
			}

			if (novasCamadasMarcador && novasCamadasMarcador.length) {
				this.buscarMarcadoresCamadas(novasCamadasMarcador);
			}

			this._reordenarCamadas();
		},

		_reordenarCamadas () {

			for (let i = 0; i < this.camadasAtivas.length; i++) {
				
				let layer = this.layers[this.camadasAtivas[i].id];
				if (layer.geom) layer.geom.bringToFront();
			}
		},

		/**
		 * Adiciona uma nova camada ao mapa.
		 */
		adicionarCamada (camada) {

			var layer = { };

			// Camada WMS
			if (camada.visualizacao.mapa) {

				if (camada.geoServer && camada.geoServer.layer) {
		
					layer.geom = this.criarCamadaWms(camada);

				} else if (camada.geoJson) {

					layer.geom = this.criarCamadaGeoJson(camada);
				}

				layer.geom.addTo(this.mapa);
			}

			if (camada.visualizacao.marcador) {

				layer.marker = L.layerGroup([]).addTo(this.mapa);
			}

			this.layers[camada.id] = layer;
			this.$bus.$emit('mapa.camadaAdicionada', camada);
		},

		/**
		 * Cria uma nova layer do leaflet a partir de uma camada WMS
		 */
		criarCamadaWms (camada) {

			var layer = L.tileLayer.wms(camadasService.WMS_URL, {
				layers: camada.geoServer.layer,
				transparent: true,
				format: 'image/png',
				uppercase: true
			});

			return layer;
		},

		/**
		 * Cria uma nova layer do leaflet a partir de uma camada GeoJSON
		 */
		criarCamadaGeoJson (camada) {

			var style = Estilo.toMapStyle(camada.estiloPadrao);
			return leafletUtils.createGeoJsonLayer(camada.geoJson, style);
		},

		/**
		 * Remove uma camada do mapa.
		 */
		removerCamada (layer) {

			if (layer.geom) this.mapa.removeLayer(layer.geom);

			if (layer.marker) this.mapa.removeLayer(layer.marker);
		},

		/* Métodos para gerenciar os marcadores das camadas */

		deveExibirMarcadores () {
			return this.mapa.getZoom() >= Config.minZoomMarkers;
		},

		limparMarcadores () {

			for (let id in this.layers) {

				let layer = this.layers[id];
				if (layer.marker) layer.marker.clearLayers();
			}
		},
		
		atualizarMarcadores () {

			if (!this.deveExibirMarcadores()) {

				if (!this.layersMarkersActive) return;
				this.limparMarcadores();
				this.layersMarkersActive = false;

			} else {

				this.layersMarkersActive = true;
				this.buscarMarcadoresCamadas();
			}
		},

		buscarMarcadoresCamadas (idsCamadas) {

			idsCamadas = idsCamadas || this.idsCamadasMarcador;

			if (!this.deveExibirMarcadores()) return;

			if (!idsCamadas || !idsCamadas.length) return;
				
			var wkt = leafletUtils.boundsToWKT(this.mapa.getBounds());
			
			camadasService.findMarcadores(idsCamadas, wkt, (data) => {
				
				if (!data || !data.length) return;

				for (let grupo of data) {
					this.adicionarMarcadoresCamada(grupo);
				}
			});
		},

		adicionarMarcadoresCamada (marcadores) {

			if (!this.deveExibirMarcadores()) return;

			var layer = this.layers[marcadores.idCamada];
			
			if (!layer || !layer.marker) return;

			layer.marker.clearLayers();
			
			var estilos = _.keyBy(marcadores.estilos, 'id');
			var bus = this.$bus;

			var onMarkerClick = function (e) {
				e.originalEvent.preventDefault();
				e.originalEvent.stopPropagation();
				bus.$emit('camada.exibirRegistro', { registro: e.target._registro });
			};

			for (let registro of marcadores.registros) {

				let estilo = estilos[registro.idEstilo];
				let coords = registro.centroid.coordinates;

				let marker = L.marker([coords[1], coords[0]], {
					icon: L.BeautifyIcon.icon(markerStyles.getMarcadorCamada(estilo)),
					draggable: false
				});

				marker._registro = registro;
				marker.addTo(layer.marker);
				marker.on('click', onMarkerClick);
				marker.on('dblclick', onMarkerClick);
			}
		},

		getEnabledDrawTool () {

			for (let toolName in this.draw.tools) {

				let tool = this.draw.tools[toolName];
				if (tool && tool._enabled) return tool;
			}
		},

		isDrawToolEnable () {

			return !!this.getEnabledDrawTool();
		},

		/**
		 * Injeta os métodos do Vuex que serão utilizados pela classe
		 */
		...mapMutations([
			'setMinZoom',
			'setMaxZoom',
			'setMapZoom',
			'setCoordinate',
			'setBaseMap'
		])
	},

	mounted () {

		if (!this.mapa) {
			this.createMap();
		}
	},

	created () {

		// Busca o base layer default
		this.selectedBaseMap = _.find(Config.baseMaps, (baseMap) => {

			if (baseMap.default) {
				this.setBaseMap(baseMap);
			}

			return baseMap.default === true;
		});
	},

	computed: {
		
		idsCamadasMarcador () {

			if (!this.layers) return null;

			var ids = [];

			for (let id in this.layers) {

				if (this.layers[id].marker) ids.push(id);
			}

			return ids;
		},

		...mapState({
			mapZoom: state => state.mapa.maxZoom,
			camadasAtivas: state => state.camadasAtivas || []
		})
	},

	watch: {
		camadasAtivas () {
			this.atualizarCamadasAtivas();
		}
	}
};

</script>

<style <style lang="scss">

@import '../styles/variaveis';
@import '../styles/mixins';

#mapa {
	width: 100%;
	height: 100%;
	cursor: default;

	.minimized-bottomleft {
		margin-left: -4px;
		margin-bottom: -5px;
	}

	.leaflet-contextmenu-item {

		color: $context-menu-item-color;

		&.over {
			background-color: $context-menu-item-hover-bg;
			color: $context-menu-item-hover-color;
		}
	}

	.leaflet-div-icon.leaflet-editing-icon.leaflet-interactive {
		background-color: rgba(0, 0, 0, 0);
		margin-left: -5px !important;
		margin-top: -5px !important;
		width: 10px !important;
		height: 10px !important;
		@include border-radius(50%);
		border: solid 2px $mapa-draw-vertex-color;

		&.leaflet-editing-icon-polygon:nth-child(2) {
			background-color: $mapa-draw-vertex-color;
			margin-left: -6px !important;
			margin-top: -6px !important;
			width: 12px !important;
			height: 12px !important;
		}
	}
}


</style>



// WEBPACK FOOTER //
// Mapa.vue?ca563890